import { COMMA, ENTER } from '@angular/cdk/keycodes';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, ElementRef, EventEmitter, Injector, Input, Output, ViewChild } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatChipInputEvent } from '@angular/material/chips';
import { MatDialog } from '@angular/material/dialog';
import { MatSelect } from '@angular/material/select';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { EMAIL_REGEX, PASSWORD_REGEX, PHONE_REGEX } from 'submodules/engine-common/regex';
import { formatDate, formatDateTime, formatDateTimeDownToMillis, formatDateTimeDownToSeconds, formatTime, getSysdate, getSystemTimeZone, wait, workingTimeZoneToUtc } from '../../../../../../submodules/common/util';
import { LocaleDataService } from '../../../../submodules/angular-common/locale-data.service';
import { BaseView } from '../../../../submodules/angular-ui-common/base-view';
import { SelectableOption } from '../../base-types';
import { ChangeCause } from '../../change-cause';
import { DialogComponent } from '../../dialog/dialog.component';
import { FieldType } from '../../field-type';
import { FilePreviewerComponent } from '../../file-previewer/file-previewer.component';
import { buildSelectableOptions, formatInteger } from '../forms-util';
import { FormFieldSettings } from './data/form-field-settings';
import { FormFieldState } from './data/form-field-state';
import { FormFieldDataService } from './form-field-data.service';
import { ImagePickerDialogComponent } from './image-picker-dialog/image-picker-dialog.component';

@Component({
  selector: 'app-form-field',
  templateUrl: './form-field.component.html',
  styleUrls: ['./form-field.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class FormFieldComponent extends BaseView {

  _initialized = false;

  /************************************************************************************************
   ************************************************************************************************
   *
   * Aspect attributes
   *
   ************************************************************************************************
   ***********************************************************************************************/

  @Input() width: string;

  /************************************************************************************************
   ************************************************************************************************
   *
   * Other attributes
   *
   ************************************************************************************************
   ***********************************************************************************************/

  @Input() set type(type: FieldType){
    // console.log('set type: ',type);
    this._type = type as FieldType;
  };
  get type() {
    return this._type;
  }

  @Input() aggregate: boolean = false;

  @Input() set label(label: string) {
    this._label = label;
  }
  get label(): string {
    return this._label;
  }
  
  @Input() mode: 'standard' | 'grid' | 'card' | 'filter' = 'standard'
  @Input() hint: string;
  @Input() set value(value: number | string | boolean | (string |number)[] | File | File[]){
    
    // console.log('set value');
    if (value==='' //Funziona solo con === e non con ==
        || (value && value.toString().includes('CLEANING AT'))) //stinga "speciale" che serve come istruzione per pulire il campo
      value=[FieldType.CHECKBOX,FieldType.SLIDE_TOGGLE].includes(this._type)?false:null;

    if ([FieldType.NUMBER, FieldType.AMOUNT].includes(this._type)) {
      if(![undefined, null].includes(value)) {
        const candidate: number = Number(value);
        if(isNaN(candidate)) 
          this._value = null;
        else {
          this._value = candidate.toFixed(this.type==FieldType.AMOUNT?2:this._settings.decimalPlaces)
        }
          
      } else this._value = value
    }
    else if (this._type == FieldType.DATE) {
      if(![undefined, null].includes(value)) {
        try { 
          this._value = (new Date(value as string)).toISOString()
          this._datePickerValue = new Date(this._value);
        } catch (error) {
          this._value = null;
        }
      } else this._value = value
    }
    else if (this._type == FieldType.CHECKBOX_TREE) {
      // console.log(`FormFieldComponent "${this.label}" - set value (FieldType.CHECKBOX_TREE)`);
      this.checkboxTreeSelection = value as (string |number)[]
      //NOTA: in questo caso non bisogna aggiornare this._value perchè deve essere settato di conseguenza
      //dal valore emesso dal checkbox tree
    }
    else
      this._value = value

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SET_FUNCTION_CALL)
  }
  get value(){
    return this._value;
  }
  set editMode(editMode: boolean){
    this._editMode = editMode;
    if(this._editMode) {
      if(this.inputFormField)
        //Lo stato di focus dell'input text rimane disallineato (forse per un susseguirsi di eventi successivi legati alla
        //presenza di altri input text material) e deve essere forzato a valle (dopo un millisecondo) lavorando direttamente sul DOM
        setTimeout(() => this.inputFormField.nativeElement.focus(), 1);
      if(this.selectFormField) {
        this.selectFormField.focus()
        this.selectFormField.open()
      }
    }
    else{
      if(this.inputFormField) this.inputFormField.nativeElement.blur()
      if(this.selectFormField) this.selectFormField.close()
    }
  }
  get editMode(){
    return this._editMode;
  }
  set toBeValidated(toBeValidated: boolean){
    this._toBeValidated = toBeValidated
  }
  get toBeValidated(){
    return this._toBeValidated
  }
  @Input() set settings(settings: FormFieldSettings){
    // this._initAttributes();

    if(settings){
      if(settings.placeholder) this._settings.placeholder = settings.placeholder
      if(settings.required) {
        this._required = true
      }
      if(settings.notEmpty) {
        this._settings.notEmpty = settings.notEmpty
      }
      if(settings.regex){
        this._settings.regex = settings.regex
      }
      if([FieldType.EMAIL, FieldType.PHONE, FieldType.PASSWORD].includes(this._type)){
        this._settings.allowBlanks = false
      }
      if(![undefined, null].includes(settings.allowBlanks)){
        this._settings.allowBlanks = settings.allowBlanks
      }
      if(settings.range){
        if(settings.range.min) this._settings.range.min = settings.range.min
        if(settings.range.max) this._settings.range.max = settings.range.max
      }
      if(settings.decimalPlaces) this._settings.decimalPlaces = settings.decimalPlaces
      if(settings.absoluteTimeCoordinate) this._settings.absoluteTimeCoordinate = settings.absoluteTimeCoordinate
      if(settings.workingTimeZone) this._settings.workingTimeZone = settings.workingTimeZone
      if(settings.multipleFileSelection) this._settings.multipleFileSelection = settings.multipleFileSelection
      if(settings.selectableOptions || settings.selectableOptionsSettings){
        if(!this._settings.selectableOptionsSettings) 
          this._settings.selectableOptionsSettings = {}
        if(!settings.selectableOptionsSettings) 
          settings.selectableOptionsSettings = {}
        if(settings.selectableOptionsSettings.selectableOptionIdFieldName) 
          this._settings.selectableOptionsSettings.selectableOptionIdFieldName = settings.selectableOptionsSettings.selectableOptionIdFieldName
        if(settings.selectableOptionsSettings.selectableOptionLabelFieldName) 
          this._settings.selectableOptionsSettings.selectableOptionLabelFieldName = settings.selectableOptionsSettings.selectableOptionLabelFieldName
        if(settings.selectableOptionsSettings.selectableOptionLabelBuilderFunction) 
          this._settings.selectableOptionsSettings.selectableOptionLabelBuilderFunction = settings.selectableOptionsSettings.selectableOptionLabelBuilderFunction
        if(settings.selectableOptionsSettings.selectableOptionShortLabelFieldName) 
          this._settings.selectableOptionsSettings.selectableOptionShortLabelFieldName = settings.selectableOptionsSettings.selectableOptionShortLabelFieldName
        if(settings.selectableOptionsSettings.selectableOptionShortLabelBuilderFunction) 
          this._settings.selectableOptionsSettings.selectableOptionShortLabelBuilderFunction = settings.selectableOptionsSettings.selectableOptionShortLabelBuilderFunction
        if(settings.selectableOptionsSettings.selectableOptionFilterFunction) 
          this._settings.selectableOptionsSettings.selectableOptionFilterFunction = settings.selectableOptionsSettings.selectableOptionFilterFunction
        
        if(settings.selectableOptions){ //nel caso delle LOV questa logica viene eseguita dopo la selezione delle righe dalla LOV
          const tmp = buildSelectableOptions(settings.selectableOptions, settings.selectableOptionsSettings);
          this._selectableOptions = tmp.selectableOptions;
          this._selectableOptionsMap = tmp.selectableOptionsMap;
        }
        if(settings.lovComponent)
          this._settings.lovComponent = settings.lovComponent
        if(this._type == FieldType.CHECKBOX_TREE) {
          this.checkboxTreeSelectableOptions = this._selectableOptions;
          //this.checkboxTree.selectableOptions = this._selectableOptions;
        }
        this._injector.get(ChangeDetectorRef).detectChanges() //senza questo comando le checkbox di CHECKBOX_GROUP non si visualizzano
      }
    }
  }
  get settings(){
    return this._settings
  }
  set error(error: string){
    this._error = error
  }
  get error(){
    return this._error
  }
  @Input() set hidden(hidden: boolean){
    this._hidden = hidden
  }
  get hidden(){
    return this._hidden;
  }
  @Input() set readOnly(readOnly: boolean){
    this._readOnly = readOnly
  }
  get readOnly(){
    return this._readOnly
  }
  @Input() set required(required: boolean){
    this._required = required
  }
  get required(){
    return this._required
  }
  set passwordHidden(passwordHidden: boolean){
    this._passwordHidden = passwordHidden
  }
  get passwordHidden(){
    return this._passwordHidden
  }
  set datePickerValue(datePickerValue: Date){
    this._datePickerValue = datePickerValue
  }
  get datePickerValue(){
    return this._datePickerValue
  }
  set candidateValue(candidateValue: string){
    this._candidateValue = candidateValue
  }
  get candidateValue(){
    return this._candidateValue
  }
  // get fileName(){
  //   return this._fileName;
  // }
  get selectableOptions() {
    return this._selectableOptions;
  }
  get selectableOptionsMap() {
    return this._selectableOptionsMap;
  }
  @Output() modelChange: EventEmitter<FormFieldState> = new EventEmitter<FormFieldState>();
  @Output() enterPressure: EventEmitter<void> = new EventEmitter<void>();

  @ViewChild('inputFormField', {static: false}) inputFormField: ElementRef;
  @ViewChild('selectFormField', {static: false}) selectFormField: MatSelect;
  //@ViewChild('checkboxTree', {static: false}) checkboxTree: CheckboxTreeComponent;

  /**
   * @deprecated: spostare su settings
   */
  private _type: FieldType = FieldType.STRING; //tipo di default
  /**
   * @deprecated: spostare su settings
   */
  private _label: string;
  /**
   * @updatableInput
   */
  private _value: number | string | boolean | (string|number)[] | File | File[]
  private _selectableOptions: SelectableOption[]
  private _selectableOptionsMap: {[key: string]: SelectableOption} | {[key: number]: SelectableOption}
  private _datePickerValue: Date;
  private _candidateValue: string;
  // private _fileName: string;
  private _editMode: boolean;      //true se il campo ha il focus ed è quindi in fase di modifica. Può essere passato a true per settare il focus di default su quel field
  private _toBeValidated: boolean; //se true (dopo il primo blur) il field deve essere validato
  /**
   * @updatableInput
   */
  private _error: string;
  private _hidden: boolean;
  private _readOnly: boolean;      //se vale true il campo non è modificabile (sola lettura). Se il form è in modalità di visualizzazione tutti i field hanno readOnly=TRUE
  private _required: boolean;
  /**
   * @onetimeInput
   */
  private _settings: FormFieldSettings = {range:{}} //inizializza lo scheletro vuoto
  private _passwordHidden: boolean = true;
  private _backgroundColor: string;

  /** Questo è un setter non standard. Si tratta di un espediente per poter settare degli attributi di componente senza dover 
   * ricorrere ad un viewChild
   */
  @Input() public set specifiedAttributes(keyValueSet: {[key: string]: any}){
    if(keyValueSet && keyValueSet.hasOwnProperty('toBeValidated')) {
      this._toBeValidated = keyValueSet.toBeValidated;
      this._emitFormFieldState(ChangeCause.VALIDATION_ACTIVATION)
    }
    if(keyValueSet && keyValueSet.hasOwnProperty('readOnly')) {
      this._readOnly = keyValueSet.readOnly;
      this._emitFormFieldState(ChangeCause.READ_ONLY_CHANGE)
    }
    if(keyValueSet && keyValueSet.hasOwnProperty('required')) {
      this._required = keyValueSet.required;
      this._emitFormFieldState(ChangeCause.REQUIRED_CHANGE)
    }
    if(keyValueSet && keyValueSet.hasOwnProperty('hidden')) {
      this._hidden = keyValueSet.hidden;
      this._emitFormFieldState(ChangeCause.HIDDEN_CHANGE)
    }
    if(keyValueSet && keyValueSet.hasOwnProperty('editMode')) {
      this.editMode = keyValueSet.editMode; //chiamo il setter. Da qui posso farlo perchè è un settaggio dall'esterno
      this._emitFormFieldState(ChangeCause.EDIT_MODE_CHANGE)
    }
  }

  public COMMA = COMMA;
  public ENTER = ENTER;

  constructor(
    public formFieldDataService: FormFieldDataService,
    protected _injector: Injector) 
  { 
    super(_injector)
    //this._backgroundColor = materialColor();
    this._initAttributes()
    this._initialized=true; //senza questa riga la griglia non esce dallo stato di caricamento (pallini che girano)
    this._emitFormFieldState(ChangeCause.MODEL_CHANGE_AFTER_CONSTRUCTOR)
  }

  // Da usare per effettuare inizializzazioni indipendenti dall'input del componente
  ngOnChanges() {
    this._injector.get(ChangeDetectorRef).detectChanges()
  }

  //se vale true mostra hint o error (che se presente ha la priorità su hint)
  isFormFieldHintToBeShown = (): boolean => !this._readOnly



















  
  /************************************************************************************************
   ************************************************************************************************
   *
   * Input Text Based SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/
  /**
   * Precondizioni: deve esserci un _value
   * Ritorna una stringa da visualizzare nel form field (che non viene memorizzata su nessuna variabile di classe)
   */
  formatFormField = (): string => {
    let locale = this._injector.get(LocaleDataService).locale
    let result;

    if (this._type == FieldType.NUMBER) {
      if (this._editMode)
        result = this._value ? this._value.toString() : null;
      else {
        //TODO: estendere ad altri LOCALE
        // if (locale.startsWith("it"))
        //   result = this._value ? formatInteger(this._value).split(".").join(",") : null;
        // else {
          if(!this._value)
            result = null
          else {
            const fragments = this._value.toString().split(".")
            let tmpResult = formatInteger(fragments[0])
            if(fragments[1])
              tmpResult += '.'+fragments[1];
            result = tmpResult;
          }
        // }
      }
    } else if ( [FieldType.INTEGER].includes(this._type) ) {
      if (this._editMode)
        result = ![undefined, null].includes(this._value) ? this._value.toString() : null;
      else
        result = ![undefined, null].includes(this._value) ? formatInteger(this._value) : null;
    } else if ( [FieldType.YEAR].includes(this._type) ) {
        result = ![undefined, null].includes(this._value) ? this._value.toString() : null;
    } else if ([FieldType.DATE, FieldType.TIME, FieldType.DATETIME, FieldType.DATETIME_DOWN_TO_SECONDS, FieldType.DATETIME_DOWN_TO_MILLIS].includes(this._type)) {
      if (this._type == FieldType.DATE) { 
        if (this._value) {
          const workingTimeZone = this._settings.absoluteTimeCoordinate && this._settings.workingTimeZone ? this._settings.workingTimeZone
                                  : this._settings.absoluteTimeCoordinate && !this._settings.workingTimeZone ? getSystemTimeZone()
                                  : 'UTC' //!this._settings.absoluteTimeCoordinate
          const separator = this._editMode ? '' : '/'
          result = formatDate(this._value, workingTimeZone, locale, separator)
        } else
          result = "";
      } else if (this._type == FieldType.TIME) {
        if (this._value) {
          // console.log('formatTime :>> ', this._value);
          const timeSeparator = this._editMode ? '' : ':'
          result = formatTime(this._value, locale, timeSeparator)
        } else
          result = "";
      } else if (this._type == FieldType.DATETIME) {
        if (this._value) {
          const workingTimeZone = this._settings.absoluteTimeCoordinate && this._settings.workingTimeZone ? this._settings.workingTimeZone
                                  : this._settings.absoluteTimeCoordinate && !this._settings.workingTimeZone ? getSystemTimeZone()
                                  : 'UTC' //!this._settings.absoluteTimeCoordinate
          const dateSeparator = this._editMode ? '' : '/'
          const dateTimeSeparator = this._editMode ? '' : ' '
          const timeSeparator = this._editMode ? '' : ':'
          result = formatDateTime(this._value, workingTimeZone, locale, dateSeparator, dateTimeSeparator, timeSeparator)
        } else
          result = "";
      } else if (this._type == FieldType.DATETIME_DOWN_TO_SECONDS) {
        if (this._value) {
          const workingTimeZone = this._settings.absoluteTimeCoordinate && this._settings.workingTimeZone ? this._settings.workingTimeZone
                                  : this._settings.absoluteTimeCoordinate && !this._settings.workingTimeZone ? getSystemTimeZone()
                                  : 'UTC' //!this._settings.absoluteTimeCoordinate
          const dateSeparator = '/'
          const dateTimeSeparator = ' '
          const timeSeparator = ':'
          result = formatDateTimeDownToSeconds(this._value, workingTimeZone, locale, dateSeparator, dateTimeSeparator, timeSeparator)
        } else
          result = "";
      } else if (this._type == FieldType.DATETIME_DOWN_TO_MILLIS) {
        if (this._value) {
          const workingTimeZone = this._settings.absoluteTimeCoordinate && this._settings.workingTimeZone ? this._settings.workingTimeZone
                                  : this._settings.absoluteTimeCoordinate && !this._settings.workingTimeZone ? getSystemTimeZone()
                                  : 'UTC' //!this._settings.absoluteTimeCoordinate
          const dateSeparator = '/'
          const dateTimeSeparator = ' '
          const timeSeparator = ':'
          const millisSeparator = '.'
          result = formatDateTimeDownToMillis(this._value, workingTimeZone, locale, dateSeparator, dateTimeSeparator, timeSeparator, millisSeparator)
        } else
          result = "";
      }
    }else
      result = this._value ? this._value.toString() : null;

    return result;
  }


  /**
   * Ribalta il contenuto dell'input text negli attribute di pertinenza:
   * - in caso di stringhe e numeri (dopo aver filtrato i caratteri non ammessi) -> _value
   * - in caso di coordinate temporali -> _candidateValue
   */
  onFormFieldInputChange = async (event) => {
    //console.log('keydown|drop :', event.target.value);
    //Sia in caso di drop che in caso di keydown è necessario attendere qualche millisecondo prima di avere il valore aggiornato
    //ALERT: Fino a 2 millis di attesa si è verificato con una certa frequenza che il valore non risultasse ancora aggiornato. 
    //Con 10 millis ci sono ancora problemi con ipad e iphone
    //Con 20 millis ci sono ancora problemi con ipad e iphone su alcuni tasti (per esempio i numeri posti sopra le lettere digitabili trascinando dall'alto verso il basso)
    await wait(30)
    //console.log('keydown|drop (after 30 millis):', event.target.value);
    let tmpValue = event.target.value;

    const REAL_TIME_CHECKED_TYPES: FieldType[] =  [FieldType.STRING, FieldType.ALPHA_STRING, FieldType.EMAIL, FieldType.PHONE, 
                                                   FieldType.PASSWORD, FieldType.TEXT_AREA,
                                                   FieldType.INTEGER, FieldType.NUMBER, FieldType.AMOUNT, FieldType.YEAR,
                                                   FieldType.FILE, FieldType.LOV]
    
    const MASK_BASED_TYPES: FieldType[] = [FieldType.DATE, FieldType.TIME, FieldType.DATETIME] 

    if(REAL_TIME_CHECKED_TYPES.includes(this._type)){

      // console.log(`this._settings.allowBlanks :>> ${this._settings.allowBlanks} for label: ${this._label}`);

      if(!this._settings.allowBlanks)
        tmpValue = tmpValue.split(" ").join('');  // rimuove tutti i blank

      if (FieldType.FILE==this._type) {
        console.log('onFormFieldInputChange :>> ');
        // Se c'è un input change per un input di tipo file, vuol dire che è stata fatta una digitazione a mano dentro il campo.
        // Al momento in questo caso svuotiamo il campo stesso.
        //TODO: andrebbe mantenuto il valore precedente oppure svuotato il campo solo in caso di cancellazione
        tmpValue = null;
      } else if (FieldType.ALPHA_STRING==this._type) {
        tmpValue = tmpValue.split(/[^a-zA-Z]+/g).join('');  // rimuove tutti i caratteri non alpha
      } else if (FieldType.NUMBER==this._type) {
        tmpValue = tmpValue.split(',').join('.')
                           .split(/[^0-9.]+/g).join('')     // rimuove tutti i caratteri che non siano '.' o numeri

        if(tmpValue.length>1)
          tmpValue = tmpValue.split(/^00+/g).join('0')      // se la stringa inizia con una sequenza di 0, lascia un solo 0
        if(tmpValue.length>1) 
          tmpValue = tmpValue.split(/^0/g).join('')         // se lo 0 iniziale rimasto non è l'unico carattere rimasto, allora lo rimuove

        tmpValue = tmpValue.split(/^[.]+/g).join('0.')      // se la stringa inizia un . ci mette uno zero davanti
        {// rimuove tutti i separatori decimali duplicati
          tmpValue = tmpValue.replace(/[.]/, "DECIMAL_SEPARATOR");
          tmpValue = tmpValue.split(/[.]+/g).join('')
          tmpValue = tmpValue.split("DECIMAL_SEPARATOR").join('.')
        }
        
      } else if ([FieldType.INTEGER, FieldType.YEAR].includes(this._type)) {
        tmpValue = tmpValue.split(/[^0-9]+/g).join('')      // rimuove tutti i caratteri che non siano numeri

        if(tmpValue.length>1)
          tmpValue = tmpValue.split(/^00+/g).join('0')      // se la stringa inizia con una sequenza di 0, lascia un solo 0
        if(tmpValue.length>1) 
          tmpValue = tmpValue.split(/^0/g).join('')         // se lo 0 iniziale rimasto non è l'unico carattere rimasto, allora lo rimuove
      }

      this._value = tmpValue!='' ? tmpValue : null;
      this.inputFormField.nativeElement.value = this._value
    }
    else if (MASK_BASED_TYPES.includes(this._type)) {
      //value non presente (null o stringa vuota)
      if (!tmpValue) {
        //Il campo _candidateValue è stato svuotato dall'utente e va valorizzato con stringa vuota in modo che quando verrà fatto onBlur 
        //si possa distinguere da un campo che era già vuoto (chè sarà null anzichè "")
        this._candidateValue = "" 
        //Il campo _dateValue deve essere settato a utc sysdate (calcolato a partire da sysdate del workingTimeZone) in modo che il picker proponga 
        //come default la data odierna
        this._datePickerValue=new Date(getSysdate(this._settings.workingTimeZone).slice(0,10))
      }                            //... 
      else this._candidateValue = tmpValue;

      // A seguito di un drag&drop si deve forzare un blur (che altrimenti non avverrebbe mai)
      if (event instanceof DragEvent)
        this.onFormFieldBlur()

      //console.log('onFormFieldInputChange END - this._candidateValue :>> ', this._candidateValue);
    }

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_KEY_DOWN)
  }




















  /************************************************************************************************
   ************************************************************************************************
   *
   * SELECT BASED
   *
   ************************************************************************************************
   ***********************************************************************************************/

  onMatSelectModelChange = (event) => {
    console.log('onMatSelectModelChange :', JSON.stringify(event));

    this._value = event;

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SELECTABLE_OPTION_SELECTION)
  } 




















  /************************************************************************************************
   ************************************************************************************************
   *
   * DATE AND TIME
   *
   ************************************************************************************************
   ***********************************************************************************************/

  getDateMask = () => {
    let mask;
    if (["en-us", "it-it", "en-gb", "ar-sa"].includes('it-it'/*TODO: prendere da localeDataService*/))
      mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/]
    else // TODO
      mask = 'error'
    return { mask: mask }
  }

  getTimeMask = () => {
    const mask = [/\d/, /\d/, ':', /\d/, /\d/]
    return { mask: mask }
  }

  getDatetimeMask = () => {
    let mask;
    if (["en-us", "it-it", "en-gb", "ar-sa"].includes('it-it'/*TODO: prendere da localeDataService*/))
      mask = [/\d/, /\d/, '/', /\d/, /\d/, '/', /\d/, /\d/, /\d/, /\d/, ' ', /\d/, /\d/, ':', /\d/, /\d/]
    else // TODO
      mask = 'error'
    return { mask: mask }
  }

  onDatePickerChange = (event) => {
    // console.log('event :>> ', event);
    // console.log('event.value.toDate() :>> ', event.value.toDate());
    // const day = event.value.toDate().getDate();
    // const month = event.value.toDate().getMonth() + 1;
    // const year = event.value.toDate().getFullYear();

    // let value = `${year}-${month.toString().padStart(2, '0')}-${day.toString().padStart(2, '0')}`
    // if(this._type == FieldType.DATETIME)
    //   value+="T00:00:00.000Z"
    // this._value = value; 

    this._value = workingTimeZoneToUtc(event.value.toDate().toISOString(), this._settings.workingTimeZone) 

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_DATE_SELECTION_FROM_PICKER)
  }

  


















  /************************************************************************************************
   ************************************************************************************************
   *
   * CHIPS AUTOCOMPLETE
   *
   ************************************************************************************************
   ***********************************************************************************************/

  /**
   * Data Model dell'input text
   */
  chipInputFormControl = new FormControl();
  /**
   * Observable agli item presenti nella listbox (quindi progressivamente scremati inserendo caratteri nella input text, che svolgono funzione di filtro)
   */
  filteredItems: Observable<Object[]>;

  //@ViewChild('autocomplete', {static: false}) autocomplete: MatAutocomplete;

  ngAfterViewInit(): void {
    this.filteredItems = this.chipInputFormControl.valueChanges.pipe(
                            map((item: string | SelectableOption) => item ? this._filter(item) : [])
                         );
  }

  /**
   * Scatta quando viene selezionato un elemento dalla lista valori. Scatta prima di 'inputTokenEnd'
   */
  selected(event: MatAutocompleteSelectedEvent): void {
    const selectedMatOptionValue: SelectableOption = event.option.value; //<--il value del mat-option selezionato
    const selectedMatOptionId = selectedMatOptionValue.id;

    const isPresentYet = ((this._value as (string|number)[]) || []).some(el=>el == selectedMatOptionId)
    if(!isPresentYet) {
      if(this._value == null) 
        this._value=[];
      (this._value as (string|number)[]).push(selectedMatOptionId)
    }

    this.inputFormField.nativeElement.value = '';
    this.chipInputFormControl.setValue(null);
    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SELECTABLE_OPTION_SELECTION);
  }

  /**
   * Scatta quando viene rimosso un chip dall'input text
   */
  remove(itemToRemoveId: string|number): void {
    this._value = (this._value as (string|number)[]).filter(el=>el!=itemToRemoveId)
    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SELECTABLE_OPTION_SELECTION);
  }

  /**
   * Quando si digita un carattere, qui arriva in input la stringa digitata fino a quel momento
   * Quando si seleziona un item, qui arriva l'oggetto selezionato (il value del mat-option)
   */
  private _filter(value: string | SelectableOption): SelectableOption[] {
    if(this._settings.selectableOptionsSettings.selectableOptionFilterFunction)
      return this._settings.selectableOptionsSettings.selectableOptionFilterFunction(this._selectableOptions, value)
    else {
      const valueAsString = typeof value === 'string' 
        ? (value as string)                  // se è una stringa
        : (value as SelectableOption).label; // se è un Oggetto

      return this._selectableOptions.filter(
        item => item.label.toLowerCase()
                          .indexOf(valueAsString.toLowerCase()) == 0
      );
    }
  }
  
  onChipsInputDrop = async (event) => {
    //In caso di drop è necessario attendere un instante prima di avere il valore
    await wait(10)

    let value = event.target.value;
    console.log('droppedItem :', event.target);
  }

  /**
   * @UNUSED La parte commentata non sembra servire (potendo fare le stesse cose in selected()), perchè nell'esempio di angular material venivano fatte qui quelle operazioni?
   * 
   * Scatenato quando 
   * - si preme invio avendo il focus sull'input text
   * - si clicca fuori dall'input text avendo il focus sull'input text stesso.
   * - si seleziona un item dalla tendina.
   */
  inputTokenEnd(event: MatChipInputEvent): void {
    console.log('ChipsAutocompleteComponent.inputTokenEnd() - event: ', event);
  }




















  /************************************************************************************************
   ************************************************************************************************
   *
   * Button Group Based SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

  isCheckboxChecked = (selectableOption) => this._value && (this._value as (string|number)[]).includes(selectableOption.id)

  isRadioButtonChecked = (selectableOption) => this._value && (this._value == selectableOption.id)

  onOptionChange = (selectableOptionId: number | string) => {
    // console.log('onOptionChange :', selectableOptionId);

    if (this._type == FieldType.CHECKBOX_GROUP) {
      if ((this._value as any[]).includes(selectableOptionId))
        this._value = (this._value as any[]).filter(el => el != selectableOptionId)
      else
        (this._value as any[]).push(selectableOptionId)
    }
    else if (this._type == FieldType.RADIO_GROUP) {
      this._value = selectableOptionId;
    }

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SELECTABLE_OPTION_SELECTION)
  } 

  checkboxTreeSelectableOptions;
  checkboxTreeSelection;

  onCheckboxTreeSelectionChange = (currentSelection: (string|number)[]) => {
    console.log('onCheckboxTreeSelectionChange - currentSelection: ', currentSelection);
    this._value = currentSelection;
    
    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_SELECTABLE_OPTION_SELECTION)
  }




















  /************************************************************************************************
   ************************************************************************************************
   *
   * File Upload Based SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

  onFileSelected = (event) => {
    if(this._type == FieldType.IMAGE) {
      const file: File = event.target.files[0]
      // this._fileName = file.name;

      const reader = new FileReader();
      reader.addEventListener('load', (event: any)=>{
        const base64 = event.target.result
        let dialogRef = this._injector.get(MatDialog).open(ImagePickerDialogComponent, 
                                          {disableClose: false,
                                           data: { imgUrl: base64 }
                                          });
        dialogRef.afterClosed().subscribe(result => {
          console.log('value :', result.out);
          this._value = result.out;
        });
      })
      reader.readAsDataURL(file)
    } else {
      //Se è stata effettuata una selezione
      if(event.target.files.length){
        if (this._settings.multipleFileSelection)
          this._value = event.target.files as File[]
        else{
          this._value = event.target.files[0] as File
        }
      }
    }

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_FILE_SELECTION)
  }

  showFilePreview() {
      let dialogRef = this._injector.get(MatDialog).open(DialogComponent, 
                                        {width: '500px',
                                         maxWidth: '100vw',
                                         disableClose: false,
                                         data: {
                                            componentInput: this._value as File,
                                            component: FilePreviewerComponent
                                         }});
  }

  clearFile = () => {
    this._value = null;
    // this._fileName = null;
  }

  onFileUploadKeyDown = async (event) => {
    this.inputFormField.nativeElement.value = (this._value as File).name
  }




















  /************************************************************************************************
   ************************************************************************************************
   *
   * LOV SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/

  onLovOpen = (event) => {

    const dialogRef = this._injector.get(MatDialog).open(DialogComponent,
      {
        width: '700px',
        maxWidth: '100vw', //l'intera larghezza della viewport. Viene usata nel caso la vieport sia più stretta della width (500px)
        disableClose: false,
        data: {
          title: "Lista",
          showActions: false, // <- i bottoni sono lasciati DENTRO il form e non nella dialog
          showCancel: false,  // <- i bottoni sono lasciati DENTRO il form e non nella dialog
          component: this._settings.lovComponent,//BasicGridShowcaseComponent,
          componentInput: null
        }
      });

    // Registrarsi per recepire l'ouptut della  dialog (che è a sua volta l'ouput del componente wrappato dalla dialog)
    this._subscription.add(dialogRef.componentInstance.output.subscribe(result => {
      console.log('LOV result :>> ', result);
      console.log('this.settings.selectableOptionsSettings :>> ', this.settings.selectableOptionsSettings);
      const tmp = buildSelectableOptions([result], this.settings.selectableOptionsSettings);
      this._selectableOptions = tmp.selectableOptions;
      this._selectableOptionsMap = tmp.selectableOptionsMap;
      this._value = result.id;
    }));
    // Registrarsi per recepire l'exit della  dialog (che può essere a sua volta l'exit del componente wrappato dalla dialog)
    this._subscription.add(dialogRef.componentInstance.exit.subscribe(() => {
    }));                                     
  }



















  /************************************************************************************************
   ************************************************************************************************
   *
   * Button Based SECTION
   *
   ************************************************************************************************
   ***********************************************************************************************/
  
   onToggle = (value) => {
    this._value = value;
    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_TOGGLE)
  }




















  emitEnterPressure = () => this.enterPressure.emit();

  onFormFieldFocus = () => {
    this._editMode = true; 
    this._emitFormFieldState(ChangeCause.FOCUS_CHANGE)
  }

  onFormFieldBlur = async () => {
    let locale = this._injector.get(LocaleDataService).locale

    this._editMode = false;
    this._toBeValidated = true;

    if ([FieldType.STRING, FieldType.ALPHA_STRING, FieldType.TEXT_AREA, FieldType.PASSWORD, FieldType.PHONE].includes(this._type)){
      this._value = this.inputFormField.nativeElement.value;
    }

    if ([FieldType.EMAIL].includes(this._type)){
      this._value = this.inputFormField.nativeElement.value.toLowerCase();
    }

    if ([FieldType.NUMBER, FieldType.AMOUNT].includes(this._type)) {
      if (this._value != null)
        this._value = Number(this._value).toFixed(this._type==FieldType.AMOUNT?2:this._settings.decimalPlaces)
    }

    else if ([FieldType.INTEGER, FieldType.YEAR].includes(this._type)) { //TODO
      // if (this._value != null)
      //   this._value = Number(this._value).toFixed(0)
    }

    else if ([FieldType.DATE, FieldType.TIME, FieldType.DATETIME].includes(this._type)) {

      //Se field.candidateValue è null significa che l'utente non ha modificato in nessun modo 
      //il contenuto del campo (sequenza focus-blur). Non deve essere effettuata nessuna operazione
      if (this._candidateValue == null) {
        this._emitFormFieldState(ChangeCause.FOCUS_CHANGE)
        return;
      }
      //Se field.candidateValue è stringa vuota significa che l'utente ha svuotato il campo (che non era vuoto).
      //Il modello deve essere svuotato
      else if (this._candidateValue == "") {
        this._value = null;
      }
      //Altrimenti l'utente ha modificato il contenuto del campo (non svuotandolo).
      //Il modello deve essere aggiornato in base al valore di field._candidateValue 
      else {
        const manageDatePart = (datePartFromMask: string)=>{
          //sostituisce i placeholder ("_") della mask con "0"
          let candidateValue = datePartFromMask.split("_").join("0");
            
          let day, month, year;

          //In base al locale il campo field._candidateValue viene interpretato secondo i seguenti pattern:
          //it-IT -> dd/mm/yyyy 
          //en-GB -> dd/mm/yyyy 
          //en-US -> mm/dd/yyyy
          //ar-SA -> dd/mm/yyyy
          //In base al locale si estrapolano dal campo field._candidateValue i valori di anno, mese e giorno
          if (locale == "en-us") {
            const fragments = candidateValue.split("/");
            year = fragments[2]; month = fragments[0]; day = fragments[1];
          }
          else if (["it-it", "en-gb", "ar-sa"].includes(locale)) {
            const fragments = candidateValue.split("/");
            year = fragments[2]; month = fragments[1]; day = fragments[0];
          }

          //Corregge giorno, mese, anno applicando un range di valori ammessi:
          //se anno = 0                                                           viene forzato all'anno corrente
          //se mese = 0                                                           viene forzato a 1
          //se mese > 12                                                          viene forzato a 12
          //se giorno = 0                                                         viene forzato a 1 
          //se giorno > numero di giorni possibili per quel mese di quell'anno    viene forzato al max possibile
          if (year == '0000') year = new Date().getFullYear().toString();
          else if (Number(year) < 100) year = "0100" //limite inferiore del picker

          if (month == '00') month = '01';
          else if (Number(month) > 12) month = "12"

          if (day == '00') day = '01';
          else {
            const lastDayOfMonth = new Date(Number(year), Number(month), 0).getDate();
            if ((Number(day)) > lastDayOfMonth)
              day = lastDayOfMonth.toString();
          }

          //Setta il field.dateValue in base al this.value
          this._datePickerValue = new Date(`${year}-${month}-${day}`);

          //Setta il field.value componendo i frammenti nel formato interno (YYYY-MM-DD)
          return `${year}-${month}-${day}`;
        }
        const manageTimePart = (timePartFromMask: string)=>{
          // console.log('manageTimePart :>> ', timePartFromMask);
          //sostituisce i placeholder ("_") della mask con "0"
          let candidateValue = timePartFromMask.split("_").join("0");
  
          let hh, mi;
  
          const fragments = candidateValue.split(":");
          hh = fragments[0]; mi = fragments[1];
  
          //Corregge hh, mi applicando un range di valori ammessi: 
          //se hh > 23                                                       viene forzato a 23
          //se mi > 59                                                       viene forzato a 59 
          if (hh > '23') hh = '23';
          if (mi > '59') mi = '59';
  
          //Setta il field.value componendo i frammenti nel formato interno (hh:mi:ss)
          return `${hh}:${mi}:00`;
        }

        let tmpValue;

        if([FieldType.DATE, FieldType.DATETIME].includes(this._type)){
          if (this._type == FieldType.DATE) {
            tmpValue = manageDatePart(this._candidateValue)
          }
          else{ //DATETIME
            const dateOut = manageDatePart(this._candidateValue.substring(0,10))
            const timeOut = manageTimePart(this._candidateValue.substring(11,19))
            tmpValue = `${dateOut}T${timeOut}.000Z`
          }
          this._value = workingTimeZoneToUtc(tmpValue, this._settings.workingTimeZone)
        }
        else { //TIME
          tmpValue = manageTimePart(this._candidateValue)

          // console.log('manageTimePart :>> ', tmpValue);

          this._value = tmpValue

          // console.log('this._value :>> ', this._value);

        }

        // if(this.formFieldDataService.DEBUG_MODE)
        //   console.log('onFormFieldBlur - tmpValue: ',tmpValue)

        // if(this.formFieldDataService.DEBUG_MODE)
        //   console.log('onFormFieldBlur - this._settings.workingTimeZone: ',this._settings.workingTimeZone)
      }

      //Resettiamo il field.candidateValue perchè si esce dall'edit mode. 
      //Quando si entra in edit mode ci si aspetta che field._candidateValue sia null
      this._candidateValue = null;
    }

    else {//TODO: serve?
      if (this._value==='')
        this._value=null;
    }

    this._emitFormFieldState(ChangeCause.FOCUS_CHANGE)
  }

  /**
   * Inizializza gli attributi del FormField con dei valori di default
   */
  private _initAttributes = () => {
    this._editMode = false
    this._toBeValidated = false
    this._hidden = false
    this._readOnly = false //TODO: cambiare a true?
    this._required = false
    this._value = null;
    if (FieldType.DATE == this._type) {
      this._candidateValue = null;
      this._datePickerValue = null;
    }
    if (FieldType.TIME == this._type) {
      this._candidateValue = null;
    }
    this._settings.placeholder = null;
    this._settings.notEmpty = false
    this._settings.allowBlanks= [FieldType.EMAIL,FieldType.PHONE,FieldType.PASSWORD].includes(this._type)?false:true
    this._settings.range = { min: -Infinity, max: Infinity }
    this._settings.decimalPlaces = this.formFieldDataService.settings.decimalPlaces
    this._settings.workingTimeZone = null //null implica l'uso del TimeZone di sistema
    this._settings.absoluteTimeCoordinate = true
    this._settings.multipleFileSelection = false
  }

  /**
   * Prepara una nuova istanza di FormField da inviare al parent tramite emit()
   */
  private _validateAndPrepareOutput = (changeCause?: ChangeCause): FormFieldState => {
    this._validateFormField()
    //console.log(`Emit caused by: ${changeCause}. Value: ${JSON.stringify(this._value)}`);
    return {
      value: this._value,
      editMode: this._editMode,
      error: this._error,
      changeCause: changeCause
    } as FormFieldState
  }

  private _emitFormFieldState(changeCause?: ChangeCause) {
    if(this._initialized)
      this.modelChange.emit(this._validateAndPrepareOutput(changeCause))
  }

  private _validateFormField = () => {
    // Indica se sono presenti errori in questa sessione di validazione
    // Attenzione: il campo _error può contenere errori settati durante la sessione di validazione
    // precedente. Al termine della sessione corrente il contenuto del campo _error può essere
    // (a)svuotato (b)cambiato (c)confermato
    let errorPresent = false;
    
    const requiredCheck = () => {
      if (this._required) {
        if (
            [undefined, null].includes(this._value) 
            || (typeof this._value == "string" && this._value.toString() == "")
        ){
          this._error = 'ERRORS.REQUIRED';
          errorPresent = true;
        }
      }
    }

    const notEmptyCheck = () => { //Ha senso solo per i dynamicFormField
      if (this._settings.notEmpty) {
        if (
            [undefined, null].includes(this._value) 
            || (typeof this._value == "string" && this._value.toString() == "")
        ){
          this._error = 'ERRORS.EMPTY';
          errorPresent = true;
        }
      }
    }

    //Controlli a livello di regex (se specificata nei settings) e di conformità di natura dato (ed. campo EMAIL)
    const semanticCheck = () => {
      if(this._value){
        
        if (this._type == FieldType.EMAIL) {
          if (!EMAIL_REGEX.test(this._value as string)) {
            this._error = 'ERRORS.EMAIL_FORMAT';
            errorPresent = true;
          }
        }

        if (this._type == FieldType.PHONE) {
          if (!(this._value as string).startsWith('+')) {
            this._error = 'ERRORS.PHONE_FORMAT.MISSING_INTERNATIONAL_PREFIX';
            errorPresent = true;
          }
          else if (!PHONE_REGEX.test(this._value as string)) {
            this._error = 'ERRORS.PHONE_FORMAT';
            errorPresent = true;
          }
        }

        if (this._type == FieldType.PASSWORD) {
          if(!this._settings.regex){ //si applica la REGEX di default solo se non specificata una regex nei settings
            if (!PASSWORD_REGEX.test(this._value as string)) {
              this._error = 'ERRORS.PASSWORD_FORMAT';
              errorPresent = true;
            }
          }
        }

        if (this._type == FieldType.ACKNOWLEDGEMENT_CHECKBOX) {
          if(this._value!=true){
            this._error = 'ERRORS.NOT_ADMITTED_VALUE';
            errorPresent = true;
          }
        }

        if(this._settings.regex){
          if (!this._settings.regex.test(this._value as string)) {
            this._error = 'ERRORS.FORMAT';
            errorPresent = true;
          }
        }
      }
    }

    const lengthAndRangeCheck = () => {
      if([FieldType.STRING, FieldType.EMAIL, FieldType.PHONE].includes(this._type)){
        if (! (
                (this._value + '').length >= this._settings.range.min 
                && (this._value + '').length <= this._settings.range.max
              )
        ){
          this._error = 'ERRORS.LENGTH';
          errorPresent = true;
        }
      }
      else if ([FieldType.INTEGER, FieldType.YEAR].includes(this._type)) {
        if (! (
                Number(this._value) != NaN && this._value >= this._settings.range.min 
                && this._value <= this._settings.range.max
              )
        ){
          this._error = 'ERRORS.NUMBER_RANGE';
          errorPresent = true;
        }
      }
    }

    requiredCheck();
    if(!errorPresent)
      notEmptyCheck();
    if(!errorPresent)
      semanticCheck();
    if(!errorPresent)
      lengthAndRangeCheck();

    if(!errorPresent || !this._toBeValidated)
      this._error = null;
  }





  onNumberPickerUp = () => {
    this._value = (this.value as number)+1

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_NUMBER_PICKER_CLICK)
  }

  onNumberPickerDown = () => {
    this._value = (this.value as number)-1

    this._emitFormFieldState(ChangeCause.VALUE_CHANGE_AFTER_NUMBER_PICKER_CLICK)
  }






}
